import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Heading, Text } from "components";
import TopMenuBar from "components/TopMenuBar";
import FooterSection from "components/FooterSection";
import { fetchBlogPosts } from "services/blogApi";

const PLATFORM_LABEL = {
    substack: "Substack",
    linkedin: "LinkedIn",
};

const mdComponents = {
    h1: ({ children }) => (
        <Heading size="xl" as="h1" className="!text-gray-300 mt-8 mb-4 leading-[130%]">{children}</Heading>
    ),
    h2: ({ children }) => (
        <Heading size="lg" as="h2" className="!text-gray-300 mt-8 mb-3 leading-[130%]">{children}</Heading>
    ),
    h3: ({ children }) => (
        <Heading size="s" as="h3" className="!text-gray-300 mt-6 mb-2 leading-[130%]">{children}</Heading>
    ),
    p: ({ children }) => (
        <Text as="p" className="leading-[170%] mb-4">{children}</Text>
    ),
    ul: ({ children }) => (
        <ul className="list-disc list-inside mb-4 space-y-1 text-gray-300">{children}</ul>
    ),
    ol: ({ children }) => (
        <ol className="list-decimal list-inside mb-4 space-y-1 text-gray-300">{children}</ol>
    ),
    li: ({ children }) => (
        <li className="leading-[160%] text-sm">{children}</li>
    ),
    blockquote: ({ children }) => (
        <blockquote className="border-l-2 border-gray-800 pl-4 my-4 italic text-gray-300">{children}</blockquote>
    ),
    code: ({ inline, children }) =>
        inline ? (
            <code className="bg-gray-900_02 text-blue-200 px-1 rounded text-sm font-mono">{children}</code>
        ) : (
            <pre className="bg-gray-900_02 rounded-lg p-4 overflow-x-auto mb-4">
                <code className="text-sm font-mono text-gray-300">{children}</code>
            </pre>
        ),
    a: ({ href, children }) => (
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
            {children}
        </a>
    ),
    hr: () => <hr className="border-gray-800 my-8" />,
    img: ({ src, alt }) => (
        <img src={src} alt={alt} className="rounded-lg my-6 max-w-full" />
    ),
};

export default function BlogPost() {
    const { postId } = useParams();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchBlogPosts()
            .then((posts) => {
                const found = posts.find((p) => p.id === postId);
                if (!found) throw new Error("Post not found");
                setPost(found);
            })
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, [postId]);

    const date = (post?.published_at || post?.created_at)
        ? new Date(post.published_at || post.created_at).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
          })
        : null;

    return (
        <>
            <Helmet>
                <title>{post ? `${post.title} — AIHumanity` : "Blog — AIHumanity"}</title>
            </Helmet>
            <div className="w-full min-h-screen bg-black-900_02">
                <TopMenuBar />
                <main className="max-w-[720px] mx-auto px-5 py-16">
                    <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-gray-300 hover:text-white-A700 mb-10 transition-colors">
                        ← All posts
                    </Link>

                    {loading && (
                        <Text as="p" className="text-gray-800 mt-6">Loading…</Text>
                    )}

                    {error && (
                        <Text as="p" className="text-red-400 mt-6">{error}</Text>
                    )}

                    {post && (
                        <article className="flex flex-col gap-6">
                            <div className="flex flex-col gap-3">
                                <div className="flex items-center gap-3 flex-wrap">
                                    {date && (
                                        <span className="text-xs text-gray-800">{date}</span>
                                    )}
                                    {post.external_links.map(({ platform, url }) => (
                                        <a
                                            key={platform}
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs px-2 py-0.5 border border-gray-800 rounded text-gray-300 hover:border-gray-300 transition-colors"
                                        >
                                            Read on {PLATFORM_LABEL[platform] ?? platform} ↗
                                        </a>
                                    ))}
                                </div>
                                <Heading size="2xl" as="h1" className="!text-gray-300 leading-[120%]">
                                    {post.title}
                                </Heading>
                            </div>

                            {post.cover_image_url && (
                                <img
                                    src={post.cover_image_url}
                                    alt={post.title}
                                    className="w-full rounded-lg object-cover max-h-[400px]"
                                />
                            )}

                            <div className="mt-2">
                                <ReactMarkdown components={mdComponents}>
                                    {post.canonical_markdown}
                                </ReactMarkdown>
                            </div>
                        </article>
                    )}
                </main>
                <FooterSection />
            </div>
        </>
    );
}
